import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft, Download, FileText, Loader2, FileArchive, Image as ImageIcon, Filter, ShieldAlert, Mail, MailX } from "lucide-react";
import { toast } from "sonner";
import EkinBrand from "@/components/EkinBrand";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function formatErr(detail) {
  if (!detail) return "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return detail?.msg || String(detail);
}

export default function Reports() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [data, setData] = useState({ count: 0, deliveries: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [emailLog, setEmailLog] = useState({ entries: [], totals: { ok: 0, failed: 0 } });
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailFilter, setEmailFilter] = useState("all"); // all | ok | failed

  const loadAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/audit-log`, { params: { action: "delivery_deleted" } });
      setAuditLog(res.data || []);
    } catch (err) {
      console.error("[Reports] loadAuditLog failed:", err);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadEmailLog = useCallback(async () => {
    setEmailLogLoading(true);
    try {
      const params = { limit: 50 };
      if (emailFilter !== "all") params.status = emailFilter;
      const res = await axios.get(`${API}/email-log`, { params });
      setEmailLog(res.data || { entries: [], totals: { ok: 0, failed: 0 } });
    } catch (err) {
      console.error("[Reports] loadEmailLog failed:", err);
    } finally {
      setEmailLogLoading(false);
    }
  }, [emailFilter]);

  const loadClientes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reports/clientes`);
      setClientes(res.data || []);
    } catch (err) {
      console.error("[Reports] loadClientes failed:", err);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (cliente) params.cliente = cliente;
      const res = await axios.get(`${API}/reports/deliveries`, { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, cliente]);

  useEffect(() => { loadClientes(); fetchReport(); loadAuditLog(); loadEmailLog(); }, [loadClientes, fetchReport, loadAuditLog, loadEmailLog]);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (cliente) p.set("cliente", cliente);
    return p.toString();
  };

  const handleExport = async (kind) => {
    setExporting(kind);
    const qs = buildParams();
    try {
      const url = `${API}/exports/${kind === "zip" ? "evidencias.zip" : `report.${kind}`}${qs ? `?${qs}` : ""}`;
      const res = await axios.get(url, { responseType: "blob" });
      const fname = kind === "zip"
        ? `evidencias_${dateFrom || "inicio"}_${dateTo || "hoy"}.zip`
        : `reporte-entregas_${dateFrom || "inicio"}_${dateTo || "hoy"}.${kind}`;
      downloadBlob(res.data, fname);
      toast.success(`Descarga lista (${kind.toUpperCase()})`);
    } catch (err) {
      // axios with responseType=blob: error body is a Blob too
      let msg = "Error al exportar";
      try {
        const blob = err?.response?.data;
        if (blob instanceof Blob) {
          const text = await blob.text();
          const json = JSON.parse(text);
          msg = formatErr(json?.detail) || msg;
        }
      } catch (parseErr) {
        console.error("[Reports] could not parse error blob:", parseErr);
      }
      toast.error(msg);
    } finally {
      setExporting(null);
    }
  };

  const downloadSingleEvidence = async (d) => {
    try {
      const url = `${API}/files/${d.evidencia_path}`;
      const res = await axios.get(url, { responseType: "blob" });
      const ext = (d.evidencia_path.split(".").pop() || "jpg").toLowerCase();
      const day = (d.entregado_at || "").slice(0, 10);
      const safeClient = (d.cliente || "").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 40);
      downloadBlob(res.data, `${day}_${safeClient}_${d.id.slice(-8)}.${ext}`);
    } catch {
      toast.error("No se pudo descargar la evidencia");
    }
  };

  const setQuickRange = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") {
      setDateFrom(iso(today)); setDateTo(iso(today));
    } else if (preset === "week") {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - day);
      setDateFrom(iso(d)); setDateTo(iso(today));
    } else if (preset === "month") {
      setDateFrom(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
      setDateTo(iso(today));
    } else if (preset === "last_month") {
      setDateFrom(iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
      setDateTo(iso(new Date(today.getFullYear(), today.getMonth(), 0)));
    } else {
      setDateFrom(""); setDateTo("");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="p-2 hover:bg-zinc-100" data-testid="btn-back-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <EkinBrand subtitle="Reportes" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport("csv")}
              disabled={exporting === "csv" || data.count === 0}
              data-testid="btn-export-csv"
              className="btn-tactile inline-flex items-center gap-2 px-3 py-2 text-xs border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40"
            >
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              CSV
            </button>
            <button
              onClick={() => handleExport("pdf")}
              disabled={exporting === "pdf" || data.count === 0}
              data-testid="btn-export-pdf"
              className="btn-tactile inline-flex items-center gap-2 px-3 py-2 text-xs bg-ekin-600 text-white hover:bg-ekin-700 disabled:opacity-40"
            >
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              PDF
            </button>
            <button
              onClick={() => handleExport("zip")}
              disabled={exporting === "zip" || data.count === 0}
              data-testid="btn-export-zip"
              className="btn-tactile inline-flex items-center gap-2 px-3 py-2 text-xs bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
            >
              {exporting === "zip" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileArchive className="h-3.5 w-3.5" />}
              Evidencias ZIP
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
        <p className="eyebrow text-zinc-500 mb-2">Reportes de operación</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">
          Entregas completadas.
        </h1>

        {/* Filters */}
        <section className="bg-white border border-zinc-200 p-5 mb-6" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-zinc-500" />
            <p className="eyebrow text-zinc-500">Filtros (por fecha de entrega real)</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="eyebrow text-zinc-600 mb-2 block">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="filter-date-from"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
              />
            </div>
            <div>
              <label className="eyebrow text-zinc-600 mb-2 block">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="filter-date-to"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
              />
            </div>
            <div>
              <label className="eyebrow text-zinc-600 mb-2 block">Cliente</label>
              <input
                type="text"
                list="clientes-datalist"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                placeholder="Todos los clientes"
                data-testid="filter-cliente"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
              />
              <datalist id="clientes-datalist">
                {clientes.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            {[
              { id: "today", label: "Hoy" },
              { id: "week", label: "Esta semana" },
              { id: "month", label: "Este mes" },
              { id: "last_month", label: "Mes anterior" },
              { id: "all", label: "Todo" },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setQuickRange(p.id)}
                data-testid={`quick-${p.id}`}
                className="btn-tactile text-xs px-3 py-1.5 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={fetchReport}
              disabled={loading}
              data-testid="btn-apply-filters"
              className="btn-tactile ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs bg-ekin-600 text-white hover:bg-ekin-700 disabled:opacity-40"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Filter className="h-3.5 w-3.5" />}
              Aplicar filtros
            </button>
          </div>
        </section>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="summary-tiles">
          <div className="bg-white p-5">
            <p className="eyebrow text-zinc-500 mb-2">Entregas en rango</p>
            <p className="font-display text-4xl font-bold leading-none" data-testid="summary-count">{data.count}</p>
          </div>
          <div className="bg-white p-5">
            <p className="eyebrow text-zinc-500 mb-2">Con evidencia fotográfica</p>
            <p className="font-display text-4xl font-bold leading-none" data-testid="summary-with-evidence">
              {data.deliveries.filter((d) => d.evidencia_path).length}
            </p>
          </div>
          <div className="bg-white p-5 col-span-2 lg:col-span-1">
            <p className="eyebrow text-zinc-500 mb-2">Sin evidencia</p>
            <p className="font-display text-4xl font-bold leading-none text-rose-600" data-testid="summary-without-evidence">
              {data.deliveries.filter((d) => !d.evidencia_path).length}
            </p>
          </div>
        </div>

        {/* Table */}
        <section className="bg-white border border-zinc-200 overflow-x-auto" data-testid="report-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left p-3 eyebrow text-zinc-500">Cliente</th>
                <th className="text-left p-3 eyebrow text-zinc-500">Herramienta</th>
                <th className="text-left p-3 eyebrow text-zinc-500">Pactada</th>
                <th className="text-left p-3 eyebrow text-zinc-500">Entregado</th>
                <th className="text-left p-3 eyebrow text-zinc-500">Recibido por</th>
                <th className="text-left p-3 eyebrow text-zinc-500">Capturó</th>
                <th className="text-right p-3 eyebrow text-zinc-500">Evidencia</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              )}
              {!loading && data.deliveries.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-sm text-zinc-500">Sin entregas para los filtros seleccionados.</td></tr>
              )}
              {!loading && data.deliveries.map((d) => (
                <tr key={d.id} data-testid={`report-row-${d.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                  <td className="p-3 font-medium">{d.cliente}</td>
                  <td className="p-3 text-zinc-700">{d.herramienta}</td>
                  <td className="p-3 text-zinc-700">{d.fecha_entrega}</td>
                  <td className="p-3 text-zinc-700">{fmtDate(d.entregado_at)}</td>
                  <td className="p-3 text-zinc-700">{d.recibido_por || "—"}</td>
                  <td className="p-3 text-zinc-700">{d.created_by_name || "—"}</td>
                  <td className="p-3 text-right">
                    {d.evidencia_path ? (
                      <button
                        onClick={() => downloadSingleEvidence(d)}
                        data-testid={`btn-download-evidence-${d.id}`}
                        className="btn-tactile inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-300 hover:bg-zinc-100"
                      >
                        <ImageIcon className="h-3.5 w-3.5" /> Descargar
                      </button>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <p className="text-xs text-zinc-500 mt-4">
          Los archivos se descargan firmados con tu sesión actual. El ZIP incluye un <code>manifest.csv</code> con la trazabilidad de cada foto.
        </p>

        {/* Audit log: deliveries deleted by ventas */}
        <section className="bg-white border border-zinc-200 mt-10" data-testid="audit-section">
          <div className="p-5 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-rose-600" />
              <p className="eyebrow text-zinc-500">Auditoría — entregas borradas por ventas</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-zinc-500" data-testid="audit-count">
                {auditLog.length} evento{auditLog.length === 1 ? "" : "s"}
              </span>
              <button
                onClick={loadAuditLog}
                disabled={auditLoading}
                data-testid="btn-refresh-audit"
                className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-2 disabled:opacity-40"
              >
                Refrescar
              </button>
            </div>
          </div>

          {auditLoading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : auditLog.length === 0 ? (
            <p className="p-8 text-sm text-zinc-500 text-center">Sin borrados registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 eyebrow text-zinc-500">Cuándo</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Quién</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Cliente</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Herramienta</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Etapa al borrar</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Fecha pactada</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((e) => (
                    <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                      <td className="p-3 text-zinc-700">{fmtDate(e.at)}</td>
                      <td className="p-3">
                        <p className="font-medium">{e.actor_name}</p>
                        <p className="text-[11px] text-zinc-500">{e.actor_email}</p>
                      </td>
                      <td className="p-3 font-medium">{e.target?.cliente || "—"}</td>
                      <td className="p-3 text-zinc-700">{e.target?.herramienta || "—"}</td>
                      <td className="p-3">
                        <span className={`eyebrow stage-${e.target?.stage || "factura"}`}>{e.target?.stage || "—"}</span>
                      </td>
                      <td className="p-3 text-zinc-700">{e.target?.fecha_entrega || "—"}</td>
                      <td className="p-3 text-[11px] text-zinc-500 font-mono">{e.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Email log: outgoing notifications */}
        <section className="bg-white border border-zinc-200 mt-10" data-testid="email-log-section">
          <div className="p-5 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-emerald-600" />
              <p className="eyebrow text-zinc-500">Notificaciones por email — últimos envíos</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 text-xs">
                <span className="px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="email-total-ok">
                  ✓ {emailLog.totals.ok} enviados
                </span>
                <span className="px-2 py-1 bg-rose-50 text-rose-800 border border-rose-200" data-testid="email-total-failed">
                  ✗ {emailLog.totals.failed} fallidos
                </span>
              </div>
              <div className="flex items-center gap-1">
                {[
                  { id: "all", label: "Todos" },
                  { id: "ok", label: "OK" },
                  { id: "failed", label: "Fallidos" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setEmailFilter(f.id)}
                    data-testid={`email-filter-${f.id}`}
                    className={`text-xs px-2.5 py-1 border transition-colors ${
                      emailFilter === f.id
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <button
                onClick={loadEmailLog}
                disabled={emailLogLoading}
                data-testid="btn-refresh-email-log"
                className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-2 disabled:opacity-40"
              >
                Refrescar
              </button>
            </div>
          </div>

          {emailLogLoading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : emailLog.entries.length === 0 ? (
            <p className="p-8 text-sm text-zinc-500 text-center">Sin notificaciones registradas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 eyebrow text-zinc-500 w-10">Status</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Cuándo</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Destinatario</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Asunto</th>
                    <th className="text-left p-3 eyebrow text-zinc-500">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLog.entries.map((e) => (
                    <tr key={e.id} data-testid={`email-row-${e.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                      <td className="p-3">
                        {e.status === "ok" ? (
                          <Mail className="h-4 w-4 text-emerald-600" data-testid="status-ok" />
                        ) : (
                          <MailX className="h-4 w-4 text-rose-600" data-testid="status-failed" />
                        )}
                      </td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{fmtDate(e.at)}</td>
                      <td className="p-3 text-zinc-900 font-medium">{e.to}</td>
                      <td className="p-3 text-zinc-700">{e.subject}</td>
                      <td className="p-3 text-[11px] text-zinc-500 font-mono max-w-md truncate" title={e.error || e.resend_id || ""}>
                        {e.status === "ok" ? `id=${e.resend_id?.slice(0, 12) || "—"}…` : (e.error || "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
